/**
 * BIGWA Core - Cache Manager
 * 
 * Orquestra múltiplas camadas de cache:
 * - L1: MemoryCache (rápido, volátil)
 * - L2: IndexedDBCache (persistente, mais lento)
 * 
 * Estratégia: Read-through, Write-through
 */

import { ok, err, AsyncResult } from '../types/result';
import { MemoryCache, MemoryCacheConfig, createMemoryCache } from './MemoryCache';
import { IndexedDBCache, IndexedDBCacheConfig, createIndexedDBCache } from './IndexedDBCache';
import { logger } from '../../utils/logger';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Configuração do Cache Manager
 */
export interface CacheManagerConfig<T> {
  /**
   * Configuração do cache L1 (Memory)
   */
  l1?: MemoryCacheConfig;

  /**
   * Configuração do cache L2 (IndexedDB)
   */
  l2?: IndexedDBCacheConfig;

  /**
   * Desabilita L1
   */
  disableL1?: boolean;

  /**
   * Desabilita L2
   */
  disableL2?: boolean;

  /**
   * Nome do cache manager para logging
   */
  name?: string;

  /**
   * Serializer customizado (para L2)
   */
  serialize?: (value: T) => string;

  /**
   * Deserializer customizado (para L2)
   */
  deserialize?: (value: string) => T;
}

/**
 * Opções para operação de get
 */
export interface GetOptions {
  /**
   * Ignora L1, vai direto para L2
   */
  skipL1?: boolean;

  /**
   * Ignora L2, só consulta L1
   */
  skipL2?: boolean;
}

/**
 * Opções para operação de set
 */
export interface SetOptions {
  /**
   * TTL específico para L1 (em ms)
   */
  l1Ttl?: number;

  /**
   * TTL específico para L2 (em ms, null = nunca expira)
   */
  l2Ttl?: number | null;

  /**
   * Não salva em L1
   */
  skipL1?: boolean;

  /**
   * Não salva em L2
   */
  skipL2?: boolean;
}

/**
 * Status do cache manager
 */
export interface CacheManagerStatus {
  l1: {
    enabled: boolean;
    entries: number;
    hitRate: number;
  };
  l2: {
    enabled: boolean;
    initialized: boolean;
  };
}

// ============================================================================
// CACHE MANAGER
// ============================================================================

/**
 * Gerenciador de cache multi-camada
 * 
 * @example
 * const cache = new CacheManager<User>({
 *   l1: { defaultTtl: 60000 },
 *   l2: { dbName: 'bigwa-users' },
 * });
 * 
 * // Get: tenta L1, depois L2, popula L1 se encontrar em L2
 * const user = await cache.get('user:123');
 * 
 * // Set: salva em L1 e L2
 * await cache.set('user:123', user);
 */
export class CacheManager<T> {
  private readonly l1: MemoryCache<T> | null;
  private readonly l2: IndexedDBCache<T> | null;
  private readonly name: string;
  private l2Initialized = false;

  constructor(config: CacheManagerConfig<T>) {
    this.name = config.name ?? 'CacheManager';

    // Inicializa L1 (Memory)
    if (!config.disableL1) {
      this.l1 = createMemoryCache<T>(config.l1);
    } else {
      this.l1 = null;
    }

    // Inicializa L2 (IndexedDB) - só se tiver configuração
    if (!config.disableL2 && config.l2) {
      this.l2 = createIndexedDBCache<T>(config.l2);
    } else {
      this.l2 = null;
    }
  }

  // ============================================================================
  // OPERAÇÕES PRINCIPAIS
  // ============================================================================

  /**
   * Obtém valor do cache (L1 → L2)
   * 
   * Se encontrar em L2, promove para L1
   */
  async get(key: string, options?: GetOptions): AsyncResult<T | null> {
    try {
      // Tenta L1 primeiro (se habilitado e não pulando)
      if (this.l1 && !options?.skipL1) {
        const l1Value = this.l1.get(key);
        
        if (l1Value !== null) {
          return ok(l1Value);
        }
      }

      // Tenta L2 (se habilitado e não pulando)
      if (this.l2 && !options?.skipL2) {
        await this.ensureL2Initialized();
        
        const l2Result = await this.l2.get(key);
        
        if (!l2Result.success) {
          return l2Result;
        }

        if (l2Result.data !== null) {
          // Promove para L1
          if (this.l1 && !options?.skipL1) {
            this.l1.set(key, l2Result.data);
          }
          
          return ok(l2Result.data);
        }
      }

      return ok(null);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Define valor no cache (L1 + L2)
   */
  async set(key: string, value: T, options?: SetOptions): AsyncResult<void> {
    try {
      // Salva em L1
      if (this.l1 && !options?.skipL1) {
        this.l1.set(key, value, options?.l1Ttl);
      }

      // Salva em L2
      if (this.l2 && !options?.skipL2) {
        await this.ensureL2Initialized();
        
        const result = await this.l2.set(key, value, options?.l2Ttl);
        
        if (!result.success) {
          return result;
        }
      }

      return ok(undefined);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Remove valor do cache (L1 + L2)
   */
  async delete(key: string): AsyncResult<void> {
    try {
      // Remove de L1
      if (this.l1) {
        this.l1.delete(key);
      }

      // Remove de L2
      if (this.l2) {
        await this.ensureL2Initialized();
        await this.l2.delete(key);
      }

      return ok(undefined);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Verifica se chave existe
   */
  async has(key: string): AsyncResult<boolean> {
    const result = await this.get(key);
    
    if (!result.success) {
      return result;
    }
    
    return ok(result.data !== null);
  }

  /**
   * Limpa todo o cache (L1 + L2)
   */
  async clear(): AsyncResult<void> {
    try {
      // Limpa L1
      if (this.l1) {
        this.l1.clear();
      }

      // Limpa L2
      if (this.l2) {
        await this.ensureL2Initialized();
        await this.l2.clear();
      }

      return ok(undefined);
    } catch (error) {
      return err(error as Error);
    }
  }

  // ============================================================================
  // OPERAÇÕES AVANÇADAS
  // ============================================================================

  /**
   * Obtém valor ou executa função para obter e cachear
   * 
   * @example
   * const user = await cache.getOrSet(
   *   'user:123',
   *   async () => await fetchUser(123),
   *   { l1Ttl: 60000, l2Ttl: 3600000 }
   * );
   */
  async getOrSet(
    key: string,
    factory: () => T | Promise<T>,
    options?: SetOptions
  ): AsyncResult<T> {
    // Tenta obter do cache
    const cached = await this.get(key);
    
    if (!cached.success) {
      return cached;
    }

    if (cached.data !== null) {
      return ok(cached.data);
    }

    // Não encontrou, executa factory
    try {
      const value = await factory();
      
      // Salva no cache
      const setResult = await this.set(key, value, options);
      
      if (!setResult.success) {
        // Log warning mas retorna o valor
        logger.warn('CacheManager', `[${this.name}] Failed to cache value for key ${key}:`, setResult.error);
      }
      
      return ok(value);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Invalida múltiplas chaves por prefixo
   */
  async invalidateByPrefix(prefix: string): AsyncResult<void> {
    try {
      // Invalida em L1
      if (this.l1) {
        this.l1.deleteByPrefix(prefix);
      }

      // Invalida em L2
      if (this.l2) {
        await this.ensureL2Initialized();
        await this.l2.deleteByPrefix(prefix);
      }

      return ok(undefined);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Atualiza valor apenas em L1 (sem persistir em L2)
   * Útil para atualizações otimistas
   */
  updateL1(key: string, value: T, ttl?: number): void {
    if (this.l1) {
      this.l1.set(key, value, ttl);
    }
  }

  /**
   * Invalida apenas L1 para uma chave
   * Próximo get irá buscar de L2
   */
  invalidateL1(key: string): void {
    if (this.l1) {
      this.l1.delete(key);
    }
  }

  // ============================================================================
  // STATUS E MANUTENÇÃO
  // ============================================================================

  /**
   * Retorna status do cache manager
   */
  getStatus(): CacheManagerStatus {
    return {
      l1: {
        enabled: this.l1 !== null,
        entries: this.l1?.size ?? 0,
        hitRate: this.l1?.getStats().hitRate ?? 0,
      },
      l2: {
        enabled: this.l2 !== null,
        initialized: this.l2Initialized,
      },
    };
  }

  /**
   * Executa limpeza de entradas expiradas
   */
  async cleanup(): AsyncResult<{ l2Cleaned: number }> {
    let l2Cleaned = 0;

    try {
      // L1 limpa automaticamente no get
      
      // L2 precisa de cleanup manual
      if (this.l2) {
        await this.ensureL2Initialized();
        const result = await this.l2.cleanup();
        
        if (result.success) {
          l2Cleaned = result.data;
        }
      }

      return ok({ l2Cleaned });
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Reseta estatísticas de L1
   */
  resetStats(): void {
    if (this.l1) {
      this.l1.resetStats();
    }
  }

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  /**
   * Inicializa L2 se ainda não foi inicializado
   */
  private async ensureL2Initialized(): Promise<void> {
    if (this.l2Initialized || !this.l2) return;
    
    await this.l2.init();
    this.l2Initialized = true;
  }

  /**
   * Fecha conexões (cleanup)
   */
  dispose(): void {
    if (this.l1) {
      this.l1.clear();
    }
    
    if (this.l2) {
      this.l2.close();
    }
  }
}

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Cria instância de CacheManager
 */
export const createCacheManager = <T>(
  config: CacheManagerConfig<T>
): CacheManager<T> => {
  return new CacheManager<T>(config);
};

/**
 * Cria CacheManager apenas com L1 (sem persistência)
 */
export const createMemoryOnlyCache = <T>(
  config?: MemoryCacheConfig
): CacheManager<T> => {
  return new CacheManager<T>({
    l1: config,
    disableL2: true,
  });
};

/**
 * Cria CacheManager com configuração padrão para Bigwa
 */
export const createBigwaCache = <T>(
  name: string,
  l1TtlMs: number = 5 * 60 * 1000, // 5 minutos
  l2TtlMs: number | null = 24 * 60 * 60 * 1000 // 1 dia
): CacheManager<T> => {
  return new CacheManager<T>({
    name,
    l1: {
      defaultTtl: l1TtlMs,
      maxEntries: 500,
      name: `${name}-L1`,
    },
    l2: {
      dbName: `bigwa-${name.toLowerCase()}`,
      storeName: 'cache',
      defaultTtl: l2TtlMs,
    },
  });
};
